import { useEffect, useState } from "react";
import api from "@/lib/api";
import { compressImage } from "@/lib/compressImage";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, MessageCircle, Trash2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

export default function Community() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [image, setImage] = useState("");
  const [commentDrafts, setCommentDrafts] = useState({});

  const load = () => api.get("/posts").then((r) => setPosts(r.data));

  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { toast.error("Max 20MB"); return; }
    try {
      const { dataUrl } = await compressImage(f);
      setImage(dataUrl);
    } catch {
      toast.error("Could not read photo");
    }
  };

  const submit = async () => {
    if (!user) { toast.error("Log in to post"); return; }
    if (!content.trim()) return;
    try {
      await api.post("/posts", { content, image: image || null });
      setContent(""); setImage("");
      load();
    } catch { toast.error("Post failed"); }
  };

  const like = async (id) => {
    if (!user) { toast.error("Log in to like"); return; }
    await api.post(`/posts/${id}/like`);
    load();
  };

  const comment = async (id) => {
    const text = (commentDrafts[id] || "").trim();
    if (!user || !text) return;
    await api.post(`/posts/${id}/comment`, { content: text });
    setCommentDrafts({ ...commentDrafts, [id]: "" });
    load();
  };

  const del = async (id) => {
    await api.delete(`/posts/${id}`);
    load();
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-12" data-testid="community-page">
      <SEO title="Community Feed" path="/community"
        description="Connect with fellow Odisha runners — share your runs, cheer on the pack, ask questions, post photos from your latest training session or race." />
      <p className="font-mono text-[11px] tracking-[0.3em] text-orange-500 uppercase">PACK FEED</p>
      <h1 className="font-display text-4xl sm:text-6xl text-white mt-2">Community</h1>
      <p className="text-zinc-400 mt-3 max-w-2xl leading-relaxed">
        The OSCR community feed — share your runs, drop your finish times, post race photos,
        cheer on a fellow runner, ask a training question or just say hi. Every post strengthens
        the pack. From first-timers to veterans, this is where Odisha's runners meet, encourage
        each other, and grow together.
      </p>

      {user && (
        <div className="hairline bg-zinc-900 p-5 mt-8" data-testid="post-composer">
          <Textarea value={content} onChange={(e) => setContent(e.target.value)}
            placeholder="What's on your run today?"
            className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600 min-h-[80px]" />
          <input type="file" accept="image/*" onChange={onFile}
            className="mt-3 block w-full text-sm text-zinc-300 file:mr-3 file:py-2 file:px-4 file:rounded-none file:border-0 file:bg-zinc-800 file:text-white file:font-display file:tracking-wider hover:file:bg-zinc-700" />
          {image && <img src={image} alt="" className="mt-3 max-h-40 hairline" />}
          <div className="mt-3 flex justify-end">
            <Button data-testid="post-submit-btn" onClick={submit} className="rounded-none bg-orange-600 hover:bg-orange-500 font-display tracking-wider">
              POST
            </Button>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-3">
        {posts.length === 0 && <div className="hairline bg-zinc-900 p-8 text-center text-zinc-500 font-mono text-sm">No posts yet. Be the first.</div>}
        {posts.map((p) => {
          const liked = user && p.likes?.includes(user.user_id);
          return (
            <article key={p.post_id} className="hairline bg-zinc-900 p-5" data-testid={`post-${p.post_id}`}>
              <header className="flex items-center gap-3">
                <Avatar className="h-9 w-9 rounded-none">
                  <AvatarImage src={p.user_picture} />
                  <AvatarFallback className="rounded-none bg-zinc-800 text-white font-display">{p.user_name?.[0]?.toUpperCase()}</AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="text-white text-sm">{p.user_name}</p>
                  <p className="font-mono text-[10px] text-zinc-500 tracking-widest uppercase">{new Date(p.created_at).toLocaleString()}</p>
                </div>
                {user && (user.user_id === p.user_id || user.role === "admin") && (
                  <button onClick={() => del(p.post_id)} className="text-zinc-500 hover:text-red-500 transition" data-testid={`delete-post-${p.post_id}`}>
                    <Trash2 size={16} />
                  </button>
                )}
              </header>
              <p className="text-zinc-200 mt-3 whitespace-pre-wrap">{p.content}</p>
              {p.image && <img src={p.image} alt="" className="mt-3 hairline max-h-96 object-cover w-full" />}
              <div className="mt-4 flex items-center gap-5">
                <button onClick={() => like(p.post_id)} className={`flex items-center gap-1 text-sm transition ${liked ? "text-orange-500" : "text-zinc-400 hover:text-white"}`} data-testid={`like-${p.post_id}`}>
                  <Heart size={16} fill={liked ? "currentColor" : "none"} /> {p.likes?.length || 0}
                </button>
                <span className="flex items-center gap-1 text-sm text-zinc-400">
                  <MessageCircle size={16} /> {p.comments?.length || 0}
                </span>
              </div>

              {p.comments?.length > 0 && (
                <div className="mt-4 border-t border-zinc-800 pt-3 space-y-2">
                  {p.comments.map((c) => (
                    <div key={c.comment_id} className="text-sm">
                      <span className="text-white">{c.user_name}</span>
                      <span className="text-zinc-400 ml-2">{c.content}</span>
                    </div>
                  ))}
                </div>
              )}

              {user && (
                <div className="mt-3 flex gap-2">
                  <Input value={commentDrafts[p.post_id] || ""}
                    onChange={(e) => setCommentDrafts({ ...commentDrafts, [p.post_id]: e.target.value })}
                    onKeyDown={(e) => e.key === "Enter" && comment(p.post_id)}
                    placeholder="Add a comment..."
                    className="rounded-none bg-zinc-950 border-zinc-800 text-white focus-visible:ring-orange-600"
                    data-testid={`comment-input-${p.post_id}`} />
                  <Button onClick={() => comment(p.post_id)} variant="outline" className="rounded-none border-zinc-700 bg-transparent text-white hover:bg-zinc-800 font-display tracking-wider">
                    SEND
                  </Button>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </div>
  );
}
